import type { Settings } from './settings';
import { DEFAULT_SETTINGS, loadSettings, mergeSettings, persist } from './settings';

export type SettingKey = keyof Settings;
type Listener = (changed: ReadonlySet<SettingKey>, settings: Settings) => void;

let current: Settings = mergeSettings(DEFAULT_SETTINGS(), loadSettings());
const listeners = new Set<Listener>();

/**
 * localStorage.setItem is a synchronous write, and the UI emits change events
 * on every pointer sample (NexusUI dials fire on each mousemove, range inputs
 * on each tick). Saving inline meant a JSON.stringify plus a disk write per
 * pointer move, which was the single largest source of drag lag. Writes are
 * coalesced into one trailing save instead.
 */
const SAVE_DEBOUNCE_MS = 300;
let saveTimer: number | null = null;

function scheduleSave(): void {
  if (saveTimer !== null) return;
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    persist(current);
  }, SAVE_DEBOUNCE_MS);
}

export function flushSave(): void {
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  persist(current);
}

export const getSettings = (): Readonly<Settings> => current;

export function get<K extends SettingKey>(key: K): Settings[K] {
  return current[key];
}

export interface UpdateOptions {
  /** Apply without persisting or notifying (used while restoring state). */
  silent?: boolean;
  /** Apply and notify, but do not persist. */
  transient?: boolean;
}

export function update(patch: Partial<Settings>, opts: UpdateOptions = {}): void {
  const changed = new Set<SettingKey>();
  for (const key of Object.keys(patch) as SettingKey[]) {
    const next = patch[key];
    if (next === undefined) continue;
    if (!Object.is(current[key], next)) {
      changed.add(key);
      (current as unknown as Record<string, unknown>)[key] = next;
    }
  }
  // Nested records are mutated in place by the UI, so callers naming them are
  // always treated as changed.
  if (patch.effects) changed.add('effects');
  if (patch.extraValues) changed.add('extraValues');

  if (opts.silent || changed.size === 0) {
    if (opts.silent) return;
  }
  if (!opts.transient) scheduleSave();
  if (changed.size === 0) return;
  for (const fn of listeners) fn(changed, current);
}

/** Signal that a nested record (effects / extraValues) was mutated in place. */
export function touch(key: SettingKey): void {
  scheduleSave();
  const changed = new Set<SettingKey>([key]);
  for (const fn of listeners) fn(changed, current);
}

export function replaceAll(next: Settings): void {
  current = next;
  scheduleSave();
  const changed = new Set<SettingKey>(Object.keys(next) as SettingKey[]);
  for (const fn of listeners) fn(changed, current);
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Subscribe to a specific set of keys. */
export function on(keys: readonly SettingKey[], fn: (settings: Settings) => void): () => void {
  return subscribe((changed, settings) => {
    for (const k of keys) {
      if (changed.has(k)) {
        fn(settings);
        return;
      }
    }
  });
}

window.addEventListener('beforeunload', flushSave);
